/**
 * FunnelReport — WHERE contacts sit in the pipeline right now, as CSS bars
 * with each stage's share of the group. These are point-in-time occupancy
 * counts (each contact is in exactly one stage), NOT cumulative reached-stage
 * conversion — so no stage-to-stage "conversion %" is shown; that would
 * routinely exceed 100% once early stages drain. 'overall' renders one
 * funnel; grouped modes render a small-multiple card per group.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { FunnelRow } from '../../types';
import { LIFECYCLES } from '../../lib';
import BarRow from './BarRow';

interface FunnelReportProps {
  groupBy: 'overall' | 'study' | 'lead_source' | 'recruiter';
  from: string | null;
  to: string | null;
  /** When set, only this group's funnel renders (use with groupBy='study'). */
  onlyGroupKey?: string | null;
  /** Scope to one study via the contact's current primary study. */
  studyId?: string | null;
}

const STAGE_COLORS: Record<string, string> = {
  new: 'bg-cyan-500', contacting: 'bg-blue-500', qualified_pending_records: 'bg-indigo-500',
  ready_to_schedule: 'bg-teal-500', scheduled: 'bg-amber-500', screened: 'bg-purple-500',
  randomized: 'bg-green-500', screen_failed: 'bg-rose-400', not_eligible: 'bg-red-400',
  not_interested: 'bg-gray-400', unreachable: 'bg-orange-400',
};

interface GroupFunnel {
  key: string;
  label: string;
  total: number;
  byStatus: Record<string, number>;
}

export default function FunnelReport({ groupBy, from, to, onlyGroupKey = null, studyId = null }: FunnelReportProps) {
  const [rows, setRows] = useState<FunnelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/api/crm/analytics/funnel', { params: { group_by: groupBy, from: from || undefined, to: to || undefined, study_id: studyId || undefined } })
      .then((res) => { if (alive) { setRows(res.data?.rows || []); setError(null); } })
      .catch((err) => { if (alive) setError(err?.response?.data?.error || 'Failed to load funnel'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [groupBy, from, to, studyId]);

  const groups: GroupFunnel[] = useMemo(() => {
    const byKey = new Map<string, GroupFunnel>();
    for (const r of rows) {
      if (onlyGroupKey && r.group_key !== onlyGroupKey) continue;
      const g = byKey.get(r.group_key) || { key: r.group_key, label: r.group_label, total: 0, byStatus: {} };
      g.byStatus[r.lifecycle_status] = (g.byStatus[r.lifecycle_status] || 0) + Number(r.total);
      g.total += Number(r.total);
      byKey.set(r.group_key, g);
    }
    return [...byKey.values()].sort((a, b) => b.total - a.total);
  }, [rows, onlyGroupKey]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading funnel…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (groups.length === 0) return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No contacts in this range yet.</p>;

  // The classic funnel reads top-down through the considering stages; terminal
  // exits render below the divider.
  const FUNNEL_ORDER = ['new', 'contacting', 'qualified_pending_records', 'ready_to_schedule', 'scheduled', 'screened', 'randomized'];
  const EXIT_ORDER = ['screen_failed', 'not_eligible', 'not_interested', 'unreachable'];

  const renderFunnel = (g: GroupFunnel) => {
    const max = Math.max(...FUNNEL_ORDER.map((s) => g.byStatus[s] || 0), 1);
    return (
      <div key={g.key} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{g.label}</h3>
          <span className="flex-shrink-0 text-xs text-gray-400">{g.total.toLocaleString()} contacts</span>
        </div>
        <div className="space-y-0.5">
          {FUNNEL_ORDER.map((status) => {
            const count = g.byStatus[status] || 0;
            const meta = LIFECYCLES.find((l) => l.value === status);
            // Honest hint: this stage's share of the group, never a pseudo
            // "conversion" (occupancy ratios can exceed 100%).
            const hint = count > 0 && g.total > 0 ? `${Math.round((count / g.total) * 100)}%` : null;
            return (
              <BarRow
                key={status}
                label={meta?.label || status}
                count={count}
                max={max}
                hint={hint}
                color={STAGE_COLORS[status] || 'bg-gray-400'}
              />
            );
          })}
        </div>
        <div className="mt-2 space-y-0.5 border-t border-gray-100 pt-2 dark:border-gray-700">
          {EXIT_ORDER.map((status) => {
            const count = g.byStatus[status] || 0;
            if (!count) return null;
            const meta = LIFECYCLES.find((l) => l.value === status);
            return (
              <BarRow
                key={status}
                label={meta?.label || status}
                count={count}
                max={Math.max(g.total, 1)}
                color={STAGE_COLORS[status] || 'bg-gray-400'}
              />
            );
          })}
        </div>
      </div>
    );
  };

  if (groupBy === 'overall' || onlyGroupKey) {
    return <div className="max-w-xl">{groups.map(renderFunnel)}</div>;
  }
  return <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{groups.map(renderFunnel)}</div>;
}
